import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  MessageCircle,
  Send,
} from "lucide-react";
import "./ContactsSection.css";
import { Helmet } from "react-helmet-async";

export function ContactsSection() {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">(
    "idle"
  );
  const [statusMessage, setStatusMessage] = useState("");

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (status !== "idle") {
      setStatus("idle");
      setStatusMessage("");
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.message) {
      setStatus("error");
      setStatusMessage(t("contacts.messages.required"));
      return;
    }

    setStatus("loading");
    setStatusMessage("");

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL;

      const res = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (res.ok) {
        setStatus("success");
        setStatusMessage(t("contacts.messages.success"));

        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        setStatus("error");
        setStatusMessage(data.error || t("contacts.messages.error"));
      }
    } catch (err) {
      console.error(err);
      setStatus("error");
      setStatusMessage(t("contacts.messages.connectionError"));
    }
  };

  const contactInfo = [
    {
      key: "phone",
      icon: <Phone className="icon" />,
      title: t("contacts.info.phone.title"),
      details: ["+351 21 123 4567", "+351 91 234 5678"],
      description: t("contacts.info.phone.description"),
    },
    {
      key: "email",
      icon: <Mail className="icon" />,
      title: t("contacts.info.email.title"),
      details: ["reservas@transferpro.pt", "info@transferpro.pt"],
      description: t("contacts.info.email.description"),
    },
    {
      key: "office",
      icon: <MapPin className="icon" />,
      title: t("contacts.info.office.title"),
      details: ["Rua das Flores, 123", "1200-000 Lisboa, Portugal"],
      description: t("contacts.info.office.description"),
    },
    {
      key: "whatsapp",
      icon: <MessageCircle className="icon" />,
      title: t("contacts.info.whatsapp.title"),
      details: ["+351 91 234 5678"],
      description: t("contacts.info.whatsapp.description"),
    },
  ];

  const officeHours = [
    {
      day: t("contacts.hours.weekdays"),
      hours: "09:00 - 18:00",
    },
    {
      day: t("contacts.hours.saturday"),
      hours: "09:00 - 14:00",
    },
    {
      day: t("contacts.hours.sunday"),
      hours: t("contacts.hours.sundayHours"),
    },
    {
      day: t("contacts.hours.holidays"),
      hours: t("contacts.hours.holidayHours"),
    },
  ];

  return (
    <>
      <Helmet>
      <title>{t("seo.contacts.title")}</title>

      <meta
        name="description"
        content={t("seo.contacts.description")}
      />

      <link
        rel="canonical"
        href="https://francescotransfers.com/contacts"
      />
    </Helmet>
    <div className="contacts">
      <div className="contacts-container">
        <div className="contacts-header">
          <h1>{t("contacts.title")}</h1>

          <p>{t("contacts.subtitle")}</p>
        </div>

        <div className="contacts-grid">
          <div className="contact-form">
            <div className="card">
              <div className="card-header">
                <Send className="icon" />
                <span>{t("contacts.form.title")}</span>
              </div>

              <div className="card-content">
                <div className="form-row">
                  <div className="form-group">
                    <label>{t("contacts.form.name")}</label>

                    <input
                      type="text"
                      placeholder={t("contacts.form.namePlaceholder")}
                      value={formData.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>{t("contacts.form.email")}</label>

                    <input
                      type="email"
                      placeholder={t("contacts.form.emailPlaceholder")}
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t("contacts.form.phone")}</label>

                    <input
                      type="text"
                      placeholder={t("contacts.form.phonePlaceholder")}
                      value={formData.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>{t("contacts.form.subject")}</label>

                    <select
                      value={formData.subject}
                      onChange={(e) =>
                        handleInputChange("subject", e.target.value)
                      }
                    >
                      <option value="">
                        {t("contacts.form.selectSubject")}
                      </option>

                      <option value="booking">
                        {t("contacts.form.subjects.booking")}
                      </option>

                      <option value="quote">
                        {t("contacts.form.subjects.quote")}
                      </option>

                      <option value="modification">
                        {t("contacts.form.subjects.modification")}
                      </option>

                      <option value="complaint">
                        {t("contacts.form.subjects.complaint")}
                      </option>

                      <option value="compliment">
                        {t("contacts.form.subjects.compliment")}
                      </option>

                      <option value="corporate">
                        {t("contacts.form.subjects.corporate")}
                      </option>

                      <option value="other">
                        {t("contacts.form.subjects.other")}
                      </option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>{t("contacts.form.message")}</label>

                  <textarea
                    placeholder={t("contacts.form.messagePlaceholder")}
                    value={formData.message}
                    onChange={(e) =>
                      handleInputChange("message", e.target.value)
                    }
                  />
                </div>

                {statusMessage && (
                  <div className={`form-status ${status}`}>
                    {statusMessage}
                  </div>
                )}

                <button
                  onClick={handleSubmit}
                  className="btn-primary"
                  disabled={status === "loading"}
                >
                  {status === "loading"
                    ? t("contacts.form.sending")
                    : t("contacts.form.send")}
                </button>
              </div>
            </div>
          </div>

          <div className="contact-info">
            <div className="info-grid">
              {contactInfo.map((contact, index) => (
                <div
                  className={`card ${
                    contact.key === "phone" || contact.key === "whatsapp"
                      ? "contact-theme-card"
                      : ""
                  }`}
                  key={index}
                >
                  <div className="card-content info-item">
                    <div className="info-icon">{contact.icon}</div>

                    <div>
                      <h3>{contact.title}</h3>

                      {contact.details.map((detail, i) => (
                        <p key={i} className="highlight">
                          {detail}
                        </p>
                      ))}

                      <p className="small">{contact.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="card office-card">
              <div className="card-header">
                <Clock className="icon" />
                <span>{t("contacts.hours.title")}</span>
              </div>

              <div className="card-content">
                {officeHours.map((schedule, i) => (
                  <div className="schedule" key={i}>
                    <span>{schedule.day}</span>
                    <span>{schedule.hours}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}